import React, { useEffect, useState } from "react";
import { useHistory } from "../../../hooks/useRouterCompat";
import { Trans, t } from '@lingui/macro';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import { Table } from "@tiptap/extension-table";
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import { useEditor } from "@tiptap/react";
import StarterKit from '@tiptap/starter-kit';
import { X, Lock, Users, User, Pin, PinOff, Trash2, Save, MoreHorizontal, ExternalLink, MessageSquare } from "lucide-react";

import { Dialog, DialogContent, DialogTitle } from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { useToast } from "../../../components/ui/use-toast";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../../components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../../../components/ui/dropdown-menu";

import Page from "../../../const/Page";
import { GetSoribotsByRoomItemResponse } from "../../../models/DTO";
import { Message } from "../../../models/Message";
import { RoomItem } from "../../../models/RoomItem";
import { Soribot } from "../../../models/Soribot";
import { UUID } from "../../../models/local/Uid";
import { Fire } from "../../../services/FireClient";
import { My } from "../../../services/My";
import { UtilApp } from "../../../services/Utils/UtilApp";
import TipTapEditor from "../../components/TipTapEditor";
import { EditorMenuBar } from "../../components/editor/EditorMenuBar";
import { useAppUIContext } from "../../../context/AppUIContext";
import { cn } from "../../../lib/utils";

const extensions = [
  StarterKit,
  Table.configure({
    resizable: true,
  }),
  TableRow,
  TableHeader,
  TableCell,
  Link.configure({
    openOnClick: true,
    autolink: true,
    linkOnPaste: true
  }),
  TaskList,
  TaskItem.configure({
    nested: true,
  }),
  Highlight.configure({ multicolor: true })
]

const MemoOpenModal: React.FC<{ item: RoomItem, addText: string | null, onClose: () => void }> = ({ item, addText, onClose }) => {
  const [title, setTitle] = useState(item.title ?? "");
  const [text, setText] = useState('...');
  const [updated, setUpdated] = useState(item.updated);
  const [editMode, setEditMode] = useState(false);
  const { toast } = useToast();
  const { workspaces } = useAppUIContext();
  const editor = useEditor({ extensions, content: '', editable: false });

  // Alert State
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertData, setAlertData] = useState({
    title: '',
    description: '',
    confirmText: t`Confirm`,
    cancelText: t`Cancel`,
    onConfirm: () => { },
    variant: 'default' as 'default' | 'destructive'
  });

  const showAlert = (title: string, description: string, onConfirm: () => void, confirmText = t`Confirm`, cancelText = t`Cancel`, variant: 'default' | 'destructive' = 'default') => {
    setAlertData({ title, description, confirmText, cancelText, onConfirm, variant });
    setAlertOpen(true);
  };

  useEffect(() => {
    setEditMode(!item.id || !!addText);
    if (addText) {
      setText(`${addText}<hr/>${item.text ?? ""}`);
    } else {
      if (!item.id) {
        setTimeout(() => {
          // Auto focus title input
          const titleInput = document.getElementById('memo-title-input');
          titleInput?.focus();
        }, 300);
      }
    }
  }, []);

  useEffect(() => {
    if (!editMode) {
      setTitle(item.title ?? "");
      setText(item.text ?? "");
    }

    // Lock check logic
    if (editMode && !item.lockedByUserId && item.id) {
      // toast({ title: t`Memo has been unlocked.`, description: t`Your changes might be overwritten by someone else.`, variant: 'destructive', duration: 4000 });
      // UX: Don't show toast immediately on switch to avoid annoyance, rely on visual lock indicator
    }
  }, [editMode]);

  // Update content when item prop changes (external update)
  useEffect(() => {
    // Only update if not editing to prevent overwriting user input
    if (!editMode && item.id) {
      setTitle(item.title ?? "");
      setText(item.text ?? "");
    }
    setUpdated(item.updated);
  }, [item.updated, item.id]); // Careful with dependencies

  useEffect(() => {
    if (editor) {
      editor.setEditable(editMode);
    }
  }, [editMode, editor]);

  function saveItem() {
    if (!editor) return;

    const htmlText = editor.getHTML();
    const saveTitle = title.trim() || t`Untitled`;

    item.title = saveTitle;
    item.text = htmlText;
    item.updated = new Date();

    const isNewItem = !item.id;
    if (isNewItem) {
      item.id = UUID.get();
      item.created = new Date();
    }
    item.lockedByUserId = null;

    Fire.item.saveRoomItem(item).then(() => {
      toast({ title: t`Memo saved`, duration: 2000 });
      // Send message logic
      const room = My.Rooms.get(item.roomId);
      if (room) {
        let msg = new Message(room, My.user);
        msg.text = item.title;
        msg.autoMessage = isNewItem ? 'memo-created' : 'memo-updated';
        Fire.msg.saveMessage(msg);
      }
    }).catch((err) => {
      toast({ title: t`Failed to save: ${err.message}`, duration: 5000 });
    });

    setText(htmlText);
    setEditMode(false);
  }

  function deleteItem() {
    showAlert(
      t`Delete memo?`,
      t`This action cannot be undone.`,
      () => {
        Fire.item.delete(item).then(() => {
          toast({ title: t`Memo deleted` });
          onClose();
        });
      },
      t`Delete`,
      t`Cancel`,
      'destructive'
    );
  }

  function closeModal() {
    if (editMode && (title !== (item.title || "") || editor?.getHTML() !== (item.text || ""))) {
      // Changes detected
      showAlert(
        t`Discard changes?`,
        t`You have unsaved changes. Are you sure you want to close?`,
        () => {
          Fire.item.setLockedBy(item, null);
          onClose();
        },
        t`Discard`,
        t`Keep Editing`,
        'destructive'
      );
    } else {
      Fire.item.setLockedBy(item, null);
      onClose();
    }
  }

  function cancelEdit() {
    if (title !== (item.title || "") || editor?.getHTML() !== (item.text || "")) {
      showAlert(
        t`Discard changes?`,
        t`Revert to previous version?`,
        () => {
          setEditMode(false);
          setTitle(item.title ?? "");
          setText(item.text ?? "");
          Fire.item.setLockedBy(item, null);
        },
        t`Discard`,
        t`Keep Editing`,
        'destructive'
      );
    } else {
      setEditMode(false);
      Fire.item.setLockedBy(item, null);
    }
  }

  function togglePin() {
    item.pinned = !item.pinned;
    Fire.item.saveRoomItem(item).then(() => {
      toast({ title: item.pinned ? t`Pinned` : t`Unpinned` });
      // Force update? Usually Fire listener handles this in parent, but for local state:
      // We aren't displaying pinned state in modal except bell icon
    });
  }

  function startEditing() {
    if (canEdit()) {
      Fire.item.setLockedBy(item, My.user.id);
      setEditMode(true);
      // Focus handled by input autoFocus or user click
    }
  }

  function canEdit() {
    return (!item.lockedByUserId || item.lockedByUserId == My.user.id)
      && (!item.editableBy
        || (item.editableBy == 'everyone' && !item.lockedByUserId || item.lockedByUserId == My.user.id)
        || (item.editableBy == 'owner-only' && item.user.id == My.user.id));
  }

  function canDelete() {
    // 작성자 본인 또는 관리자(Admin) 권한이 있는 경우 삭제 가능
    const room = My.Rooms.get(item.roomId);
    const workspace = workspaces.find(w => w.id === room?.workspaceId);
    const isOwner = workspace?.owner.id === My.user.id;
    return item.user.id === My.user.id || isOwner;
  }

  function getLockedByTextPart() {
    if (item.editableBy == 'owner-only') return null;
    if (item.lockedByUserId) {
      const userIsEditing = item.lockedByUserId == My.user.id;
      if (userIsEditing) return null; // Don't show lock warning if I am the one locking it (unless debugging)

      const user = My.getUserFromRooms(item.lockedByUserId);
      const name = (user?.name ?? t`Someone`);
      const text = t`${name} is currently editing this memo.`;

      return (
        <div className="flex items-center gap-2 p-3 bg-orange-50 text-orange-800 rounded-lg mb-4 text-sm border border-orange-100">
          <Lock className="w-4 h-4 shrink-0" />
          <span className="flex-1 font-medium">{text}</span>
          {!editMode && (
            <Button variant="ghost" size="sm" onClick={() => Fire.item.setLockedBy(item, null)} className="text-orange-700 hover:text-orange-900 hover:bg-orange-100 h-7 text-xs">
              <Trans>Unlock</Trans>
            </Button>
          )}
        </div>
      );
    }
    return null;
  }

  // --- UI Render ---
  return (
    <>
      <Dialog open={true} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="max-w-4xl h-[85vh] md:h-[90vh] flex flex-col p-0 gap-0 overflow-hidden rounded-xl bg-white shadow-2xl border-none">

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white/80 backdrop-blur-md sticky top-0 z-50">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={closeModal} className="rounded-full hover:bg-gray-100 text-gray-500">
                <X className="w-5 h-5" />
              </Button>
              <DialogTitle className="sr-only"><Trans>Memo</Trans></DialogTitle>
              {/** Visual Breadcrumb or Small Title could go here, but kept clean as per design **/}
            </div>

            <div className="flex items-center gap-2">
              {editMode ? (
                <>
                  <Select value={item.editableBy ?? 'everyone'} onValueChange={(val) => item.editableBy = val as any} disabled={item.user.id != My.user.id}>
                    <SelectTrigger className="h-8 text-xs border-none bg-gray-100 hover:bg-gray-200 rounded-full w-auto gap-1 px-3">
                      {item.editableBy === 'owner-only' ? <Lock className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="everyone"><Trans>Everyone can edit</Trans></SelectItem>
                      <SelectItem value="owner-only"><Trans>Only the creator can edit</Trans></SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={saveItem} size="sm" className="rounded-full px-5 bg-orange-500 hover:bg-orange-600 text-white font-medium shadow-sm transition-all hover:shadow-md">
                    <Trans>Save</Trans>
                  </Button>
                </>
              ) : (
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={togglePin} className="rounded-full text-gray-400 hover:text-orange-500 hover:bg-orange-50">
                    {item.pinned ? <Pin className="w-5 h-5 text-orange-500 fill-current" /> : <Pin className="w-5 h-5" />}
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="rounded-full text-gray-400 hover:text-gray-900">
                        <MoreHorizontal className="w-5 h-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={startEditing} disabled={!canEdit()} className="gap-2">
                        <MessageSquare className="w-4 h-4" /> <Trans>Edit</Trans>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={deleteItem} disabled={!canDelete()} className="gap-2 text-red-600 focus:text-red-600 focus:bg-red-50">
                        <Trash2 className="w-4 h-4" /> <Trans>Delete</Trans>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto bg-white">
            <div className="max-w-3xl mx-auto px-6 py-8 md:py-12 flex flex-col min-h-full">

              {/* Lock Warning */}
              {getLockedByTextPart()}

              {/* Title Input Area */}
              <div className="mb-6">
                {editMode ? (
                  <input
                    id="memo-title-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t`Untitled`}
                    className="w-full text-4xl py-2 font-bold text-gray-900 placeholder:text-gray-300 border-none outline-none bg-transparent"
                    autoComplete="off"
                    maxLength={100}
                  />
                ) : (
                  <h1 className="text-4xl py-2 font-bold text-gray-900 break-words leading-tight">
                    {title || <span className="text-gray-300">{t`Untitled`}</span>}
                  </h1>
                )}
              </div>

              {/* Meta Info */}
              <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gray-100">
                <Avatar className="w-10 h-10 border border-gray-100">
                  <AvatarImage src={/* TODO: User Photo */ ''} />
                  <AvatarFallback className="bg-orange-100 text-orange-600 font-medium">{item.user.name[0]}</AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-gray-900">{item.user.name}</span>
                  <span className="text-xs text-gray-500">
                    {item.updated && item.updated.toString() !== item.created.toString()
                      ? t`Updated ${UtilApp.dateStrToTimeAndDay(item.updated, My.user.lang)}`
                      : t`Created ${UtilApp.dateStrToTimeAndDay(item.created, My.user.lang)}`}
                  </span>
                </div>
                <div className="ml-auto">
                  <ConnectedSoribot item={item} />
                </div>
              </div>

              {/* Editor */}
              <div className="flex-1 prose prose-lg prose-slate max-w-none text-gray-800 leading-relaxed">
                {editMode && editor && (
                  <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm py-2 border-b border-gray-100 mb-6 transition-all rounded-lg shadow-sm">
                    <EditorMenuBar editor={editor} />
                  </div>
                )}
                <div className="min-h-[300px] outline-none">
                  <TipTapEditor editor={editor} editable={editMode} content={text} />
                </div>
              </div>

            </div>
          </div>

        </DialogContent>
      </Dialog>

      {/* Alert Dialog */}
      <AlertDialog open={alertOpen} onOpenChange={setAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{alertData.title}</AlertDialogTitle>
            <AlertDialogDescription>{alertData.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{alertData.cancelText}</AlertDialogCancel>
            <AlertDialogAction
              onClick={alertData.onConfirm}
              className={cn(alertData.variant === 'destructive' && "bg-red-500 hover:bg-red-600")}
            >
              {alertData.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default MemoOpenModal;

const ConnectedSoribot: React.FC<{ item: RoomItem }> = ({ item }) => {
  const [resp, setResp] = useState<GetSoribotsByRoomItemResponse>();
  const [alertOpen, setAlertOpen] = useState(false);
  const [selectedBot, setSelectedBot] = useState<{ id: string, name: string, imageUrl: string | null } | null>(null);

  const history = useHistory();
  const { soribots } = useAppUIContext();

  useEffect(() => {
    setResp(undefined);
    Fire.bot.getSoribotsByRoomItem({ roomItemId: item.id }).then(setResp);
  }, [item.id]);

  const handleBotClick = (s: Soribot) => {
    setSelectedBot(s);
    setAlertOpen(true);
  };

  const handleAskSoribot = () => {
    if (selectedBot) {
      history.replace(`${Page.soribot}/freechat/${selectedBot.id}`);
    }
    setAlertOpen(false);
  };

  if ((resp?.soribots.length ?? 0) > 0) {
    return (
      <div className="flex flex-wrap gap-2">
        {resp?.soribots.map(s => (
          <div
            key={s.id}
            onClick={() => handleBotClick(s as any)}
            className="flex items-center gap-2 px-3 py-1 bg-violet-50 text-violet-700 rounded-full hover:bg-violet-100 cursor-pointer transition-colors border border-violet-100"
          >
            <Avatar className="w-4 h-4">
              <AvatarImage src={s.imageUrl ?? Soribot.DEFAULT_IMAGE_URL} />
              <AvatarFallback className="text-[10px]">{s.name.substring(0, 1)}</AvatarFallback>
            </Avatar>
            <span className="text-xs font-medium">{s.name}</span>
          </div>
        ))}

        <AlertDialog open={alertOpen} onOpenChange={setAlertOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t`Soribot Info`}</AlertDialogTitle>
              <AlertDialogDescription>
                {t`This memo is added to Soribot ${selectedBot?.name}`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setAlertOpen(false)}>{t`Close`}</AlertDialogCancel>
              {selectedBot && soribots.some(s => s.id == selectedBot.id) && (
                <AlertDialogAction onClick={handleAskSoribot} className="bg-violet-600 hover:bg-violet-700">{t`Ask Soribot`}</AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  } else {
    return null;
  }
};